// src/pages/employeeCommission/MyCommissionReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FileText, BarChart2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  getMyCommissionHistory,
  getMyDetailedOrders,
} from '../../services/api/employeeCommission';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import styles from './MyCommissionReport.module.css';

const formatLKR = (amount) => {
  const num = parseFloat(amount) || 0;
  return `LKR ${num.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatRate = (rate, basis) => {
  const val = parseFloat(rate).toFixed(2);
  return basis === 'order_value' ? `${val}%` : `LKR ${val}`;
};

const basisLabel = (key) => {
  const map = { order_count: 'Per Order', order_quantity: 'Per Qty', order_value: '% Value' };
  return map[key] || key;
};

// Ensure a value like "2026-03-01T00:00:00.000Z" or "2026-03-01" always renders
// as plain YYYY-MM-DD with no time or timezone suffix.
const toDateOnly = (val) => {
  if (!val) return '';
  const str = String(val);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (str.includes('T')) return str.split('T')[0];
  const d = new Date(str);
  if (isNaN(d.getTime())) return str;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const TIME_RANGE_LABELS = {
  monthly:     'Monthly',
  fortnightly: 'Fortnightly',
  weekly:      'Weekly',
};

const MyCommissionReport = () => {
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [historyData, setHistoryData]       = useState(null);
  const [loading, setLoading]               = useState(true);
  const [downloadingIdx, setDownloadingIdx] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyCommissionHistory();
      setHistoryData(res);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to load commission history.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownloadDetail = async (period, idx) => {
    setDownloadingIdx(idx);
    try {
      const res = await getMyDetailedOrders({
        dateFrom: period.dateFrom,
        dateTo:   period.dateTo,
      });

      const rows = res.orders.map((o) => ({
        'Order Number':             o.orderNumber,
        'Airwaybill':               o.airwaybillNumber,
        'Courier':                  o.businessCourierName,
        'Items':                    o.itemsDescription,
        'Created':                  o.created   ? 'Yes' : 'No',
        'Confirmed':                o.confirmed ? 'Yes' : 'No',
        'Grand Total (LKR)':        o.grandTotal?.toFixed(2),
        'Total Qty':                o.totalQty,
        'Confirm Commission (LKR)': o.confirmCommission?.toFixed(2),
        'Create Commission (LKR)':  o.createCommission?.toFixed(2),
        'Total Commission (LKR)':   o.totalCommission?.toFixed(2),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Commission Detail');
      XLSX.writeFile(
        wb,
        `my_commission_${toDateOnly(period.dateFrom)}_${toDateOnly(period.dateTo)}.xlsx`
      );
    } catch {
      showError('Failed to export detail report.');
    } finally {
      setDownloadingIdx(null);
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>My Commission</h1>
          <p className={styles.pageSubtitle}>Your commission history</p>
        </div>
        <div className={styles.tableCard}>
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Loading commission history…</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Not eligible ──
  if (!historyData || historyData.eligible === false) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>My Commission</h1>
          <p className={styles.pageSubtitle}>Your commission history</p>
        </div>
        <div className={styles.tableCard}>
          <div className={styles.emptyState}>
            <BarChart2 size={48} />
            <p className={styles.emptyTitle}>Not a commissioned employee</p>
            <p className={styles.emptyDesc}>You are not currently configured to receive commission.</p>
          </div>
        </div>
      </div>
    );
  }

  const { employee, timeRange, calculationBasis, periods } = historyData;
  const isOrderCount    = calculationBasis === 'order_count';
  const isOrderValue    = calculationBasis === 'order_value';
  const isOrderQuantity = calculationBasis === 'order_quantity';

  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>My Commission</h1>
          <p className={styles.pageSubtitle}>
            {employee?.name}
            {employee?.employeeNumber ? ` · #${employee.employeeNumber}` : ''}
            {' · '}
            <span className={styles.periodBadge}>{TIME_RANGE_LABELS[timeRange] || timeRange}</span>
            {' · '}
            <span className={styles.basisBadge}>{basisLabel(calculationBasis)}</span>
          </p>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        <div className={styles.tableCardHeader}>
          <h3 className={styles.tableCardTitle}>
            Commission History
            <span className={styles.periodCount}>{periods.length} periods</span>
          </h3>
        </div>

        {periods.length === 0 ? (
          <div className={styles.emptyState}>
            <BarChart2 size={48} />
            <p className={styles.emptyTitle}>No commission data</p>
            <p className={styles.emptyDesc}>No delivered orders found in the last 6 months.</p>
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Date Range</th>
                  <th>Basis</th>
                  {isOrderCount    && <th>Confirmed Orders</th>}
                  {isOrderValue    && <th>Confirmed Order Value</th>}
                  {isOrderQuantity && <th>Confirmed Item Qty</th>}
                  <th>Confirm Rate</th>
                  {isOrderCount    && <th>Created Orders</th>}
                  {isOrderValue    && <th>Created Order Value</th>}
                  {isOrderQuantity && <th>Created Item Qty</th>}
                  <th>Create Rate</th>
                  <th>Total Confirm</th>
                  <th>Total Create</th>
                  <th>Total Commission</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p, idx) => {
                  const isDownloading = downloadingIdx === idx;
                  const hasActivity =
                    p.confirmedOrderCount > 0 ||
                    p.createdOrderCount   > 0 ||
                    p.totalCommission     > 0;

                  return (
                    <tr key={idx} className={hasActivity ? styles.activeRow : styles.emptyRow}>
                      <td>
                        <span className={styles.dateRange}>
                          {toDateOnly(p.dateFrom)} – {toDateOnly(p.dateTo)}
                        </span>
                      </td>
                      <td>
                        <span className={styles.basisCell}>{basisLabel(p.calculationBasis)}</span>
                      </td>

                      {isOrderCount    && <td className={styles.countCell}>{p.confirmedOrderCount}</td>}
                      {isOrderValue    && <td className={styles.numCell}>{formatLKR(p.confirmedOrderValue)}</td>}
                      {isOrderQuantity && <td className={styles.countCell}>{p.confirmedOrderQty}</td>}

                      <td className={styles.rateCell}>
                        {formatRate(p.confirmCommissionRate, p.calculationBasis)}
                      </td>

                      {isOrderCount    && <td className={styles.countCell}>{p.createdOrderCount}</td>}
                      {isOrderValue    && <td className={styles.numCell}>{formatLKR(p.createdOrderValue)}</td>}
                      {isOrderQuantity && <td className={styles.countCell}>{p.createdOrderQty}</td>}

                      <td className={styles.rateCell}>
                        {formatRate(p.createCommissionRate, p.calculationBasis)}
                      </td>

                      <td className={styles.subTotal}>{formatLKR(p.totalConfirmCommission)}</td>
                      <td className={styles.subTotal}>{formatLKR(p.totalCreateCommission)}</td>

                      <td>
                        <span className={p.totalCommission > 0 ? styles.totalHighlight : styles.totalZero}>
                          {formatLKR(p.totalCommission)}
                        </span>
                      </td>

                      <td>
                        <button
                          className={styles.btnOutline}
                          onClick={() => handleDownloadDetail(p, idx)}
                          disabled={isDownloading || !hasActivity}
                          title={hasActivity ? 'Download detail' : 'No orders in this period'}
                        >
                          {isDownloading ? (
                            <div className={styles.spinnerDark} />
                          ) : (
                            <FileText size={13} />
                          )}
                          Detail
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCommissionReport;